import { useCallback, useEffect, useRef, useState } from "react";

/**
 * useSpeech — read text aloud.
 *
 * One path: server-side TTS via POST /api/tts (one good voice on every
 * device). There is no browser-voice fallback any more (Plan H2, David's
 * call): the system voice was bad enough to be worse than silence, so when
 * the server cannot speak — 503 not configured, network trouble, autoplay
 * blocked — speak() resolves quietly and the text on screen is the question.
 *
 * `available` is false once the server has said it cannot speak (any 5xx), so
 * a page can hide its audio toggle rather than offer a choice that does nothing.
 * `supported` stays true for API compatibility.
 *
 * Faster (David, Oct 29): prepare(text) asks the server to start making the
 * audio before it's needed — call it as soon as a question's text arrives.
 * speak(text) then plays the prepared audio from a URL, and the <audio>
 * element plays the streamed MP3 as it arrives instead of waiting for the
 * whole file. A text never prepared is prepared on the spot.
 */
const PREPARED_MAX = 8;
export function useSpeech() {
  const [speaking, setSpeaking] = useState(false);
  const [available, setAvailable] = useState(true);
  const supported = true;

  const audioRef = useRef(null);
  const preparedRef = useRef(new Map());      // text -> Promise<url | null>
  const availableRef = useRef(true);

  // A SINGLE persistent <audio> element, reused across every speak() call.
  // iOS (esp. iPhone Chrome) unlocks audio per-ELEMENT, not globally: an element
  // that has played once inside a user gesture may play again later without a
  // fresh gesture, but a brand-new element created mid-flow is blocked. The old
  // code did `new Audio(url)` every call, so unlocking one element didn't help
  // the next one — which is why iPhone Chrome fell back to the tacky browser
  // voice while iPhone Safari (lenient) played the good MP3. Reusing one element
  // + unlock() below fixes that.
  function getAudioEl() {
    if (!audioRef.current) {
      audioRef.current = new Audio();
      audioRef.current.onended = () => setSpeaking(false);
      audioRef.current.onerror = () => setSpeaking(false);
    }
    return audioRef.current;
  }

  // Unlock the persistent audio element from within a user gesture. Call this
  // synchronously from a tap handler (e.g. the Impressions "Start" button)
  // BEFORE any await. It plays a silent clip on the SAME element speak() will
  // later use, marking it user-initiated so the real MP3 plays without falling
  // back to browser TTS. No-op-safe where already unlocked.
  const unlock = useCallback(() => {
    try {
      const el = getAudioEl();
      // A tiny silent MP3 (data URI). Playing it in-gesture blesses the element.
      el.src =
        "data:audio/mpeg;base64,//uQxAAAAAAAAAAAAAAAAAAAAAAAWGluZwAAAA8AAAACAAACcQCA";
      el.volume = 0;
      const p = el.play();
      // Restore volume for the real playback that follows.
      if (p && typeof p.then === "function") {
        p.then(() => { el.pause(); el.volume = 1; }).catch(() => { el.volume = 1; });
      } else {
        el.volume = 1;
      }
    } catch { /* best-effort */ }
  }, []);

  const stopAll = useCallback(() => {
    if (audioRef.current) {
      audioRef.current.pause();
      // Do NOT null the element — keep it persistent (and unlocked). Only clear
      // its source. Nulling would force a new element next time and re-lock iOS.
      try { audioRef.current.src = ""; } catch { /* ignore */ }
    }
    setSpeaking(false);
  }, []);

  const markUnavailable = useCallback(() => {
    availableRef.current = false;
    setAvailable(false);
  }, []);

  // Start making the audio for `text`; resolves to its URL, or null.
  const prepare = useCallback((text) => {
    const key = String(text || "");
    if (!key || !availableRef.current) return Promise.resolve(null);
    const have = preparedRef.current.get(key);
    if (have) return have;
    const p = (async () => {
      try {
        const res = await fetch("/api/tts/prepare", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ text: key }),
        });
        // Any server-side failure means "no voice here": not configured
        // (503), not installed (503). Stop asking rather than retrying on
        // every question with the toggle still promising audio.
        if (res.status >= 500) markUnavailable();
        if (!res.ok) return null;
        const d = await res.json();
        return d.url || null;
      } catch {
        return null;
      }
    })();
    const map = preparedRef.current;
    map.set(key, p);
    p.then(url => { if (!url) map.delete(key); });   // a failure may be retried
    while (map.size > PREPARED_MAX) map.delete(map.keys().next().value);
    return p;
  }, [markUnavailable]);

  const speak = useCallback(async (text) => {
    if (!text || !availableRef.current) return;
    stopAll();
    setSpeaking(true);
    try {
      const url = await prepare(text);
      if (!url) throw new Error("no audio");
      // Reuse the persistent (unlocked) element — do NOT create a new Audio(),
      // which iOS Chrome would treat as un-unlocked and block. onended/onerror
      // are already bound in getAudioEl(); a failed stream ends the reading.
      const audio = getAudioEl();
      audio.src = url;
      audio.volume = 1;
      await audio.play();
    } catch (e) {
      // Endpoint down / not configured / autoplay blocked → silence. The
      // words are on screen; `speaking` returns to false so any turn that
      // waits for the reading to end moves on at once.
      setSpeaking(false);
    }
  }, [stopAll, prepare]);

  const cancel = useCallback(() => stopAll(), [stopAll]);

  useEffect(() => () => stopAll(), [stopAll]);

  return { speak, prepare, cancel, unlock, speaking, supported, available };
}
